import { type Customer } from '@/common/types/customer'
import { type CaseChannelEnum } from '@/common/types/channel'
import { EntityFieldCodeEnum, type CustomFieldValueData } from '@/common/types/fields'
import { type EmergencyCodeEnum } from '@/common/types/emergencies'
import { type PriorityCodeEnum } from '@/common/types/priorities'
import { EntityFieldInjectionMethods } from '@/common/types/general'
import { type InteractionDirectionEnum, type InteractionStatusEnum, type InteractionTypeEnum } from '@/common/types/interactionProperties'

// Bulk fetch V1
export type CaseDetailsDTOV1 = {
  // basic fields without include or expand
  id: number
  currentChannelCode: CaseChannelEnum
  creator?: string
  status: CaseStatusEnum
  currentSkillCode: string
  dueDateAlert?: string
  wakeupDate?: string
  emergencyCode?: EmergencyCodeEnum
  closingDate?: string
  externalReference?: string
  parentId?: number
  description?: string
  priorityCode: PriorityCodeEnum
  contactDriverCode?: string
  teamCode?: UUID
  customerId?: number
  productCode?: string
  assignee?: string
  dueDate: string
  creationDate: string
  minPushDate?: string
  hasNonAnsweredInteractions: boolean
  // include CREATION_STATE
  creationChannelCode?: CaseChannelEnum
  screenCode?: UUID
  initialDueDate?: string
  importId?: number
  importDate?: string
  initialSkillCode?: string
  // include HAS_ATTACHMENTS
  hasAttachments?: boolean
  // include ASSIGNEES
  firstAssignee?: string
  lastAssignee?: string
  // include KEY_ACTIONS_DATA
  firstTakeonDate?: string
  lastActionComment?: string
  lastReopenActionDate?: string
  numberOfReopenActions?: number
  lastActionIdWithReason?: number
  lastActionId?: number
  lastValidationActionId?: number
  // include KEY_INTERACTIONS_DATA
  firstInteractionDirection?: InteractionDirectionEnum
  lastInboundInteractionDate?: string
  lastInteractionWith?: string
  firstInboundInteractionId?: number
  firstOutboundInteractionId?: number
  // include NB_INTERACTIONS
  numberOfInteractions?: number
  // expand CUSTOM_FIELDS
  customFields?: CustomFieldValueData[]
}

export type CaseDTOV3 = CaseDetailsDTOV1

export type Case = CaseDetailsDTOV1 & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
  // depends of include KEY_ACTIONS_DATA
  lastActionReasonNotEmpty?: string
  lastActionReason?: string
  lastActionType?: TicketActionTypeEnum
  lastActionTypeReasonNotEmpty?: TicketActionTypeEnum
  lastEventDate?: string
  lastValidationActionDate?: string
  lastValidationActionUser?: string
  lastValidationActionType?: TicketActionTypeEnum
  lastValidationActionComment?: string
  // depends of include KEY_INTERACTIONS_DATA
  firstInboundDate?: string
  firstInboundStatus?: InteractionStatusEnum
  firstInboundType?: InteractionTypeEnum
  firstOutboundDate?: string
  firstOutboundStatus?: InteractionStatusEnum
  firstOutboundType?: InteractionTypeEnum
  // by fetching /api/v3/users
  assigneeUserExternalId?: string
  assigneeUserPhone?: string
  assigneeTeamCode?: UUID
  creatorEmail?: string
  // by fetching /api/v3/users then /api/v2/teams
  assigneeUserGroupSiteName?: string
  creationUserGroupSiteName?: string
  // by fetching /api/v2/skills with include PRODUCTIVITY
  skillTargetProcessingTime?: number
  // by fetching /api/v1/bulk-customers/fetch
  customer?: Customer
}

export enum CaseIncludeEnum {
  HAS_ATTACHMENTS = 'HAS_ATTACHMENTS',
  ASSIGNEES = 'ASSIGNEES',
  CREATION_STATE = 'CREATION_STATE',
  KEY_ACTIONS_DATA = 'KEY_ACTIONS_DATA',
  KEY_INTERACTIONS_DATA = 'KEY_INTERACTIONS_DATA',
  NB_INTERACTIONS = 'NB_INTERACTIONS',
}

export enum CaseExpandEnum {
  CUSTOM_FIELDS = 'CUSTOM_FIELDS',
}

type CaseAvailableIncludeOptions = keyof typeof CaseIncludeEnum
type CaseAvailableExpandOptions = keyof typeof CaseExpandEnum

type CaseFieldInjectionMethod<T> = T extends CaseAvailableExpandOptions
  ? (typeof EntityFieldInjectionMethods)['EXPAND']
  : (typeof EntityFieldInjectionMethods)['INCLUDE']

type CaseFieldInjectionOption<T extends CaseAvailableIncludeOptions | CaseAvailableExpandOptions> = {
  scope: T
  method: CaseFieldInjectionMethod<T>
}

/**
 * Map of case fields added by an include and an expand
 */
export const CaseFieldInjectionMap = new Map<
  EntityFieldCodeEnum,
  CaseFieldInjectionOption<CaseAvailableIncludeOptions> | CaseFieldInjectionOption<CaseAvailableExpandOptions>
>([
  [EntityFieldCodeEnum.HAS_ATTACHMENTS, { scope: CaseIncludeEnum.HAS_ATTACHMENTS, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_ASSIGNEE, { scope: CaseIncludeEnum.ASSIGNEES, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_ASSIGNEE, { scope: CaseIncludeEnum.ASSIGNEES, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CREATION_CHANNEL, { scope: CaseIncludeEnum.CREATION_STATE, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.INITIAL_SKILL, { scope: CaseIncludeEnum.CREATION_STATE, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.INITIAL_SKILL_CODE, { scope: CaseIncludeEnum.CREATION_STATE, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.CREATION_SCREEN, { scope: CaseIncludeEnum.CREATION_STATE, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.IMPORT_ID, { scope: CaseIncludeEnum.CREATION_STATE, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.IMPORT_DATE, { scope: CaseIncludeEnum.CREATION_STATE, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_CLAIM_DATE, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.NB_REOPEN_ACTIONS, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_COMMENT, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_REOPEN_ACTION, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_ACTION_REASON_NOT_EMPTY, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_ACTION_TYPE, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_ACTION_TYPE_REASON_NOT_EMPTY, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_ACTION_REASON, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_EVENT_DATE, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_COMMENT, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_DATE, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_TYPE_NAME, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_VALIDATION_ACTION_USER, { scope: CaseIncludeEnum.KEY_ACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_INTERACTION_DIRECTION, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_INTERACTION_DATE, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.LAST_INTERACTION_WITH, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_OUTBOUND_STATUS, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_OUTBOUND_TYPE, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_OUTBOUND_DATE, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_INBOUND_STATUS, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_INBOUND_TYPE, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
  [EntityFieldCodeEnum.FIRST_INBOUND_DATE, { scope: CaseIncludeEnum.KEY_INTERACTIONS_DATA, method: EntityFieldInjectionMethods.INCLUDE }],
])

export type CaseFieldConfig = {
  includes?: Set<CaseIncludeEnum>
  expands?: Set<CaseExpandEnum>
}

export enum CaseStatusEnum {
  TODO = 'TODO',
  RESERVED = 'RESERVED',
  INPROGRESS = 'INPROGRESS',
  PENDING = 'PENDING',
  TOBEVALIDATED = 'TOBEVALIDATED',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  START = 'START',
}

export enum CaseStatusTypeEnum {
  OPENED = 'OPENED',
  CLOSED = 'CLOSED',
}

export const mapCaseStatusType: Record<CaseStatusTypeEnum, Array<CaseStatusEnum>> = {
  OPENED: [CaseStatusEnum.TODO, CaseStatusEnum.RESERVED, CaseStatusEnum.INPROGRESS, CaseStatusEnum.PENDING, CaseStatusEnum.TOBEVALIDATED],
  CLOSED: [CaseStatusEnum.COMPLETED, CaseStatusEnum.CANCELED],
}

export const enum WorklogItemEnum {
  HANDLE = 'HANDLE',
  CONSULT = 'CONSULT',
  CREATE = 'CREATE',
}

export const enum UserStateEnum {
  CONNECTED = 'CONNECTED',
  WORKING = 'WORKING',
  CONSULTING = 'CONSULTING',
  CREATING = 'CREATING',
}

export interface WorklogItem {
  beginDate: string
  duration: number
  id: number
  skill: string
  case: number
  type: WorklogItemEnum
  user: number
}

export interface WorklogTooltipItem {
  skill: string
  duration: number
  userState: string
}

export type MailboxDTOV1 = {
  id: number
  name: string
  emailAddress: string
  active: boolean
  spaceCode: UUID
}

export const enum TicketActionTypeEnum {
  ADD_ATTACHMENT = 'ADD_ATTACHMENT',
  APPROVE = 'APPROVE',
  ASKFORVALIDATION = 'ASKFORVALIDATION',
  ASSIGN = 'ASSIGN',
  ATTACHPARENT = 'ATTACHPARENT',
  CANCEL_ESCALATE = 'CANCEL_ESCALATE',
  COMMENT = 'COMMENT',
  COMPLETE = 'COMPLETE',
  COMPLETEONCREATION = 'COMPLETEONCREATION',
  COMPLETEONVALIDATION = 'COMPLETEONVALIDATION',
  CONVERT_TO_SMS = 'CONVERT_TO_SMS',
  CONVERT_TO_WHATSAPP = 'CONVERT_TO_WHATSAPP',
  CREATION = 'CREATION',
  DELETE = 'DELETE',
  DELETE_ATTACHMENT = 'DELETE_ATTACHMENT',
  DISAPPROVE = 'DISAPPROVE',
  ESCALATE = 'ESCALATE',
  HANDLE_INTERACTIONS = 'HANDLE_INTERACTIONS',
  IDENTIFIED_BY_IMPORT = 'IDENTIFIED_BY_IMPORT',
  MOVE_TICKET_CUSTOMER = 'MOVE_TICKET_CUSTOMER',
  PUTBACKINPOOL = 'PUTBACKINPOOL',
  REASSIGN = 'REASSIGN',
  REOPEN = 'REOPEN',
  STACK = 'STACK',
  STACKANDPUTBACKINPOOL = 'STACKANDPUTBACKINPOOL',
  STACKANDREASSIGN = 'STACKANDREASSIGN',
  STACKANDTRANSFER = 'STACKANDTRANSFER',
  TAKEON = 'TAKEON',
  TRANSFER = 'TRANSFER',
  UNDOVALIDATION = 'UNDOVALIDATION',
  UPDATEDUEDATE = 'UPDATEDUEDATE',
  UPDATEVALUES = 'UPDATEVALUES',
  WAKEUP = 'WAKEUP',
}
export type CaseActionTypeDTOV1 = TicketActionTypeEnum

export interface CaseActionParamsDTOV1 {
  onlySearchable?: boolean
}

export type TicketActionType = keyof typeof TicketActionTypeEnum

export const ExcludeFieldsForCaseDetailsCard: EntityFieldCodeEnum[] = [
  EntityFieldCodeEnum.EXTERNAL_REF,
  EntityFieldCodeEnum.PRIORITY,
  EntityFieldCodeEnum.TICKET_ID,
  EntityFieldCodeEnum.DUE_DATE,
  EntityFieldCodeEnum.TICKET_STATUS,
]
